import { useRef, useState } from 'react';
import { dicomHelpers } from '../services/dicomHelpers';
import { dedupeTags } from '../services/imagingTags';

const HYDRATE_PROGRESS_HOLD_MS = 700;
const DOWNLOAD_MAX_PCT = 84;

export function withSeriesLabels(study) {
  const series = dicomHelpers
    .assignSeriesKeys(study.series || [])
    .sort(dicomHelpers.compareSeriesOrder);
  const labels = dicomHelpers.buildSeriesLabels(series);
  return {
    ...study,
    labels,
    series: series.map((s) => ({
      ...s,
      label: labels[s.seriesKey] || 'Series',
    })),
    tags: dedupeTags(study.tags || []),
  };
}

export function formatByteSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '0 B';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function useSeriesHydration({ studies, setStudies, downloadFile }) {
  const [hydratedSeries, setHydratedSeries] = useState({});
  const [seriesHydrationProgress, setSeriesHydrationProgress] = useState({});
  const hydrateInflightRef = useRef({});

  const hydrateSeriesSlices = async (studyKey, seriesKey) => {
    const study = studies.find((s) => s.studyInstanceUID === studyKey);
    if (!study) return null;

    const seriesMeta = study.series.find((s) => s.seriesKey === seriesKey);
    if (!seriesMeta) return null;

    if (hydratedSeries[seriesKey]) {
      return { study, series: seriesMeta };
    }

    if (!hydrateInflightRef.current[seriesKey]) {
      hydrateInflightRef.current[seriesKey] = (async () => {
        const clearUi = () => {
          delete hydrateInflightRef.current[seriesKey];
          setSeriesHydrationProgress((prev) => {
            if (!prev[seriesKey]) return prev;
            const next = { ...prev };
            delete next[seriesKey];
            return next;
          });
        };

        try {
          const fileHashes = seriesMeta.fileHashes || [];
          if (!fileHashes.length) {
            clearUi();
            return null;
          }

          const fileSizes = (seriesMeta.fileSizes || []).map((s) => Number(s) || 0);
          const sizesAligned = fileSizes.length === fileHashes.length;
          const totalKnownBytes = sizesAligned
            ? fileSizes.reduce((a, b) => a + b, 0)
            : 0;

          const report = (percent, detail) => {
            setSeriesHydrationProgress((prev) => ({
              ...prev,
              [seriesKey]: {
                label: 'Loading series',
                percent: Math.max(0, Math.min(100, Math.round(percent))),
                detail: detail ?? '',
              },
            }));
          };

          const startDetail = totalKnownBytes > 0
            ? `Downloading 0 B / ${formatByteSize(totalKnownBytes)}`
            : `Downloading 0/${fileHashes.length} file(s)`;
          report(0, startDetail);

          const downloadedFiles = [];
          let bytesDoneBefore = 0;

          for (let i = 0; i < fileHashes.length; i++) {
            const expectedFileBytes = sizesAligned ? fileSizes[i] : 0;

            const onDownloadProgress = (progressEvent) => {
              const loaded = progressEvent.loaded || 0;
              const headerTotal = progressEvent.total > 0 ? progressEvent.total : 0;
              const fileTarget = expectedFileBytes > 0 ? expectedFileBytes : headerTotal;
              const cappedLoad = expectedFileBytes > 0
                ? Math.min(loaded, expectedFileBytes)
                : loaded;

              let downloadRatio;
              let detail;
              if (totalKnownBytes > 0) {
                const soFar = Math.min(bytesDoneBefore + cappedLoad, totalKnownBytes);
                downloadRatio = soFar / totalKnownBytes;
                detail = `${formatByteSize(soFar)} / ${formatByteSize(totalKnownBytes)}`;
              } else if (fileHashes.length > 0) {
                const fileFrac = fileTarget > 0 ? Math.min(1, loaded / fileTarget) : 0;
                downloadRatio = (i + fileFrac) / fileHashes.length;
                detail = `File ${i + 1}/${fileHashes.length} · ${formatByteSize(loaded)}${
                  fileTarget ? ` / ${formatByteSize(fileTarget)}` : ''
                }`;
              } else {
                downloadRatio = 0;
                detail = '';
              }

              report(downloadRatio * DOWNLOAD_MAX_PCT, detail);
            };

            const blob = await downloadFile(fileHashes[i], onDownloadProgress);
            bytesDoneBefore += blob.size;
            downloadedFiles.push(
              new File([blob], fileHashes[i] || 'image.dcm', {
                type: blob.type || 'application/dicom',
              }),
            );

            if (totalKnownBytes > 0) {
              const soFar = Math.min(bytesDoneBefore, totalKnownBytes);
              report(
                Math.min(DOWNLOAD_MAX_PCT, (soFar / totalKnownBytes) * DOWNLOAD_MAX_PCT),
                `${formatByteSize(soFar)} / ${formatByteSize(totalKnownBytes)}`,
              );
            } else {
              report(
                ((i + 1) / fileHashes.length) * DOWNLOAD_MAX_PCT,
                `Downloaded ${i + 1}/${fileHashes.length} file(s)`,
              );
            }
          }

          report(86, 'Parsing DICOM…');

          const imageData = [];
          for (let fi = 0; fi < downloadedFiles.length; fi++) {
            const file = downloadedFiles[fi];
            try {
              const newImages = await dicomHelpers.extractImageDataFromFile(file);
              for (const img of newImages) imageData.push(img);
            } catch (err) {
              console.log('fileError: ', err);
            }
            report(
              86 + ((fi + 1) / downloadedFiles.length) * 8,
              `Parsing DICOM… (${fi + 1}/${downloadedFiles.length})`,
            );
          }

          if (!imageData.length) {
            clearUi();
            return null;
          }

          const parsedStudies = dicomHelpers.extractStudiesFromImageData(imageData);
          const parsedStudy = parsedStudies.find((ps) => ps.studyInstanceUID === studyKey)
            || parsedStudies[0];
          const matchingParsedSeries = (parsedStudy?.series || [])
            .filter((ps) => ps.seriesInstanceUID === seriesMeta.seriesInstanceUID);
          const parsedSeriesParts = matchingParsedSeries.length
            ? matchingParsedSeries
            : (parsedStudy?.series || []).slice(0, 1);

          if (!parsedSeriesParts.length) {
            clearUi();
            return null;
          }

          const parsedSlices = parsedSeriesParts.flatMap((ps) => ps.slices);
          const parsedMiddleIndex = Math.floor(parsedSlices.length / 2);

          report(95, 'Preparing viewer…');

          setStudies((prev) => prev.map((s) => {
            if (s.studyInstanceUID !== studyKey) return s;
            return {
              ...s,
              series: s.series.map((se) => {
                if (se.seriesKey !== seriesKey) return se;
                return {
                  ...se,
                  slices: parsedSlices,
                  sliceCount: parsedSlices.length,
                  middleIndex: parsedMiddleIndex,
                  middleSlice: parsedSlices[parsedMiddleIndex],
                };
              }),
            };
          }));

          setHydratedSeries((prev) => ({ ...prev, [seriesKey]: true }));

          setSeriesHydrationProgress((prev) => ({
            ...prev,
            [seriesKey]: {
              label: 'Series ready',
              percent: 100,
              detail: '',
            },
          }));
          await new Promise((r) => {
            setTimeout(r, HYDRATE_PROGRESS_HOLD_MS);
          });
          clearUi();

          return {
            study,
            series: { ...seriesMeta, slices: parsedSlices },
          };
        } catch (err) {
          console.log('hydrateSeriesSlices error:', err);
          clearUi();
          return null;
        }
      })();
    }

    return hydrateInflightRef.current[seriesKey];
  };

  return { hydrateSeriesSlices, hydratedSeries, seriesHydrationProgress };
}
